import { useEffect, useState } from 'react';

const OVERFLOW_EPSILON = 1;

const useHasVerticalOverflow = (containerRef, contentRef, dependencyList = [], minWidth = 1025) => {
  const [hasVerticalOverflow, setHasVerticalOverflow] = useState(false);

  useEffect(() => {
    const containerEl = containerRef.current;
    const contentEl = contentRef.current || containerEl;

    if (!containerEl || !contentEl) {
      return undefined;
    }

    const updateOverflowState = () => {
      if (typeof window !== 'undefined' && window.innerWidth < minWidth) {
        setHasVerticalOverflow(false);
        return;
      }

      const contentRectHeight = contentEl.getBoundingClientRect().height;
      const measuredContentHeight = Math.max(contentEl.scrollHeight, Math.ceil(contentRectHeight));
      const containerHeight = containerEl.clientHeight;
      const isOverflowing = measuredContentHeight - containerHeight > OVERFLOW_EPSILON;

      setHasVerticalOverflow(isOverflowing);
    };

    updateOverflowState();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            updateOverflowState();
          })
        : null;

    if (resizeObserver) {
      resizeObserver.observe(containerEl);
      if (contentEl !== containerEl) {
        resizeObserver.observe(contentEl);
      }
    }

    window.addEventListener('resize', updateOverflowState);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', updateOverflowState);
    };
  }, [containerRef, contentRef, minWidth, ...dependencyList]);

  return hasVerticalOverflow;
};

export default useHasVerticalOverflow;
